import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { CImage, CSidebar, CNavItem, CSidebarNav } from '@coreui/react-pro'
import './SideBar.scss'
import PermissionProvider from '../PermissionProvider/PermissionProvider'
import Logo from '../../assets/Logo.svg'
import AILogo from '../../assets/BlueLogo.svg'
import { setMessages, setSelectedChat } from 'src/redux/messages/messageSlice'
import { setActiveDate, setPatientSearchText } from 'src/redux/census/censusSlice'
import { format } from 'date-fns'
import { resetState } from 'src/redux/practiceIq/PracticIqThunk'
import { setCurrentSearch } from '../../redux/encounters/encountersSlice'
import {
  setProviderHoldSearchText,
  setProviderHoldTabOpened,
} from '../../redux/providerHold/providerHoldSlice'
import { setIsSaveDraft } from '../../redux/progressNote/progressNoteSlice'
import FEATURES from '../../utils/featuresConstants'
import { getIsAi } from '../../utils/sharedFeaturesFunctions'
import { COLLECTOR_URL } from '../../apiHelper/apiRoute/collector'
import { getCollector } from '../../apiHelper/collectorApi'

const SIDE_MENU_OPTIONS = {
  DASHBOARD: 'DASHBOARD',
  CENSUS: 'CENSUS',
  ENCOUNTERS: 'ENCOUNTERS',
  PRACTICE_IQ: 'PRACTICE_IQ',
  PROVIDER_HOLD: 'PROVIDER_HOLD',
  MESSAGING: 'MESSAGING',
  PATIENTS: 'PATIENTS',
}
export default function SideMenuBar() {
  const dispatch = useDispatch()
  const location = useLocation()

  const [selectedMenuOption, setSelectedMenuOption] = useState(SIDE_MENU_OPTIONS.DASHBOARD)
  const isDashboard = selectedMenuOption === SIDE_MENU_OPTIONS.DASHBOARD
  const isCensus = selectedMenuOption === SIDE_MENU_OPTIONS.CENSUS
  const isEncounters = selectedMenuOption === SIDE_MENU_OPTIONS.ENCOUNTERS
  const isPracticeIQ = selectedMenuOption === SIDE_MENU_OPTIONS.PRACTICE_IQ
  const isProviderHold = selectedMenuOption === SIDE_MENU_OPTIONS.PROVIDER_HOLD
  const isMessaging = selectedMenuOption === SIDE_MENU_OPTIONS.MESSAGING
  const isPatients = selectedMenuOption === SIDE_MENU_OPTIONS.PATIENTS

  const features = useSelector((state) => state?.user?.features)
  const settings = useSelector((state) => state?.user?.settings)
  const censusDefaultToToday = (settings?.censusDefaultDate || 'defaultTODAY') === 'defaultTODAY'

  useEffect(() => {
    if (location.pathname === '/dashboard') {
      setSelectedMenuOption(SIDE_MENU_OPTIONS.DASHBOARD)
    } else if (location.pathname === '/census') {
      setSelectedMenuOption(SIDE_MENU_OPTIONS.CENSUS)
    } else if (location.pathname === '/encounters') {
      setSelectedMenuOption(SIDE_MENU_OPTIONS.ENCOUNTERS)
    } else if (location.pathname === '/practiceIq') {
      setSelectedMenuOption(SIDE_MENU_OPTIONS.PRACTICE_IQ)
    } else if (location.pathname === '/messages') {
      setSelectedMenuOption(SIDE_MENU_OPTIONS.MESSAGING)
    } else if (location.pathname === '/providerhold') {
      setSelectedMenuOption(SIDE_MENU_OPTIONS.PROVIDER_HOLD)
    } else if (location.pathname === '/patientList') {
      setSelectedMenuOption(SIDE_MENU_OPTIONS.PATIENTS)
    }
  }, [location.pathname])

  const onSaveIfProgressNote = () => {
    if (location.pathname.includes('progressnote')) {
      dispatch(setIsSaveDraft(true))
    }
  }

  return (
    <CSidebar
      position="fixed"
      // always hide the sidebar because of its unwanted functionality
      className={`sidebar-container sidebar-hide`}
    >
      <div>
        {/* Permissions need to be implemented based on user data */}

        <div>
          <CSidebarNav className="sidebar-content-web">
            {/* Claimocity Logo */}
            <CImage
              className="notification-icon mt-3 mb-3"
              src={getIsAi() ? AILogo : Logo}
              width={60}
              height={45}
            />

            <PermissionProvider permission={'Dashboard Tasks View'}>
              <div
                className={isDashboard ? 'sidebar-icon-active' : ''}
                onClick={() => {
                  onSaveIfProgressNote()
                  setSelectedMenuOption(SIDE_MENU_OPTIONS.DASHBOARD)
                  dispatch(setMessages([]))
                  dispatch(setSelectedChat({}))
                }}
              >
                <CNavItem href="#/dashboard">
                  <div className="sideBarIcons">
                    <i
                      className="fa-regular fa-house-blank sidebar-icon-web"
                      style={{ fontSize: '1.2em' }}
                    ></i>
                    <span className={isDashboard ? 'sideBarIconTitle-active' : 'sideBarIconTitle'}>
                      Dashboard
                    </span>
                  </div>
                </CNavItem>
              </div>
            </PermissionProvider>

            <PermissionProvider permission={'Census view'}>
              <div
                className={isCensus ? 'sidebar-icon-active' : ''}
                onClick={() => {
                  onSaveIfProgressNote()
                  setSelectedMenuOption(SIDE_MENU_OPTIONS.CENSUS)
                  dispatch(setMessages([]))
                  dispatch(setSelectedChat({}))
                  dispatch(setPatientSearchText(''))
                  if (censusDefaultToToday) {
                    dispatch(setActiveDate(format(new Date(), 'yyyy-MM-dd')))
                  }
                }}
              >
                <CNavItem href="#/census">
                  <div className="sideBarIcons">
                    <i
                      className="fa-regular fa-clipboard-list sidebar-icon-web"
                      style={{ fontSize: '1.2em' }}
                    ></i>
                    <span className={isCensus ? 'sideBarIconTitle-active' : 'sideBarIconTitle'}>
                      Census
                    </span>
                  </div>
                </CNavItem>
              </div>
            </PermissionProvider>

            <PermissionProvider permission={'Census view'}>
              <div
                className={`${isEncounters ? 'sidebar-icon-active' : ''} encounters-sideBar`}
                onClick={() => {
                  onSaveIfProgressNote()
                  setSelectedMenuOption(SIDE_MENU_OPTIONS.ENCOUNTERS)
                  dispatch(setCurrentSearch(''))
                }}
              >
                <CNavItem href="#/encounters">
                  <div className="sideBarIcons">
                    <i
                      className="fa-solid fa-list sidebar-icon-web"
                      style={{ fontSize: '1.6em' }}
                    ></i>
                    <span className={isEncounters ? 'sideBarIconTitle-active' : 'sideBarIconTitle'}>
                      Encounters
                    </span>
                  </div>
                </CNavItem>
              </div>
            </PermissionProvider>

            <PermissionProvider permission={'PracticeIQ - Provider encounters'}>
              <div
                className={isPracticeIQ ? 'sidebar-icon-active' : ''}
                onClick={() => {
                  onSaveIfProgressNote()
                  setSelectedMenuOption(SIDE_MENU_OPTIONS.PRACTICE_IQ)
                  dispatch(resetState())
                  dispatch(setMessages([]))
                  dispatch(setSelectedChat({}))
                }}
              >
                <CNavItem href="#/practiceIq">
                  <div className="sideBarIcons">
                    <i
                      className="fa-regular fa-square-poll-vertical sidebar-icon-web"
                      style={{ fontSize: '1.3em' }}
                    ></i>
                    <span className={isPracticeIQ ? 'sideBarIconTitle-active' : 'sideBarIconTitle'}>
                      Practice IQ
                    </span>
                  </div>
                </CNavItem>
              </div>
            </PermissionProvider>

            <PermissionProvider permission={'claimocity providerhold'}>
              {features?.includes(FEATURES.ProviderHold) && (
                <div
                  className={isProviderHold ? 'sidebar-icon-active' : ' '}
                  onClick={() => {
                    onSaveIfProgressNote()
                    setSelectedMenuOption(SIDE_MENU_OPTIONS.PROVIDER_HOLD)
                    dispatch(setMessages([]))
                    dispatch(setSelectedChat({}))
                    dispatch(setProviderHoldSearchText(''))
                    dispatch(setProviderHoldTabOpened())
                  }}
                >
                  <CNavItem href="#/providerhold">
                    <div className="sideBarIcons">
                      <i
                        className="fa-sharp fa-regular fa-bell sidebar-icon-web"
                        style={{ fontSize: '1.2em' }}
                      >
                        <HoldAlertDot />
                      </i>
                      <span
                        className={isProviderHold ? 'sideBarIconTitle-active' : 'sideBarIconTitle'}
                      >
                        Provider Hold
                      </span>
                    </div>
                  </CNavItem>
                </div>
              )}
            </PermissionProvider>

            <PermissionProvider permission={'Dashboard Tasks View'}>
              <div
                className={isMessaging ? 'sidebar-icon-active' : ''}
                onClick={() => {
                  onSaveIfProgressNote()
                  setSelectedMenuOption(SIDE_MENU_OPTIONS.MESSAGING)
                }}
              >
                <CNavItem href="#/messages">
                  <div className="sideBarIcons">
                    <i
                      className="fa-regular fa-message-lines sidebar-icon-web"
                      style={{ fontSize: '1.2em' }}
                    ></i>
                    <span className={isMessaging ? 'sideBarIconTitle-active' : 'sideBarIconTitle'}>
                      Messaging
                    </span>
                  </div>
                </CNavItem>
              </div>
            </PermissionProvider>

            <div
              className={isPatients ? 'sidebar-icon-active' : ''}
              onClick={() => {
                onSaveIfProgressNote()
                setSelectedMenuOption(SIDE_MENU_OPTIONS.PATIENTS)
              }}
            >
              <CNavItem href="#/patientList">
                <div className="sideBarIcons">
                  <i
                    className="fa-sharp fa-magnifying-glass sidebar-icon-web"
                    style={{ display: 'flex', justifyContent: 'center' }}
                  ></i>
                  <span className={isPatients ? 'sideBarIconTitle-active' : 'sideBarIconTitle'}>
                    Patients
                  </span>
                </div>
              </CNavItem>
            </div>
          </CSidebarNav>
        </div>
      </div>
    </CSidebar>
  )
}

function HoldAlertDot() {
  const providerId = useSelector((state) => state?.user?.provider?._id)
  const [show, setShow] = useState(false)
  const hasOpenedTab =
    localStorage.getItem(SIDE_MENU_OPTIONS.PROVIDER_HOLD) === SIDE_MENU_OPTIONS.PROVIDER_HOLD

  useEffect(() => {
    if (!hasOpenedTab) {
      getCollector(
        COLLECTOR_URL.GET_PROVIDER_HOLD_DATA.replace('{startIndex}', '0')
          .replace('{recordCount}', '1')
          .replace('{scope}', 'provider'),
      ).then((res) => {
        if (res?.status === 'SUCCESS') {
          setShow(res?.data.total > 0)
        }
      })
    }
  }, [hasOpenedTab, providerId])

  return show && !hasOpenedTab ? <div className={'alertDot'}></div> : null
}
